import { Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CardV2 as Card, Label, Text, Well } from '@feathr/components';

import AdPreview from '../AdPreview';
import type { IAdGroup, IAdGroupRowErrors } from '../AdStep.types';
import AdGroupKeywords from './AdGroupKeywords';
import AdGroupSitelinks from './AdGroupSitelinks';

interface IAdGroupPreviewProps {
  adGroup: IAdGroup;
  /** Per-row messages, so the summary can mark a problem without being expanded. */
  rowErrors: IAdGroupRowErrors;
  /** The advertiser name Google shows above each ad's display URL. */
  businessName?: string;
}

/**
 * The collapsed summary of one ad group: the keywords and sitelinks its ads share, then
 * the same live Google preview of each ad that the editor shows.
 *
 * @param props - {@link IAdGroupPreviewProps}.
 */
function AdGroupPreview({
  adGroup,
  rowErrors,
  businessName,
}: Readonly<IAdGroupPreviewProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  return (
    <>
      <Card.Content>
        <Label weight={'medium'}>{t('Ad group')}</Label>
        <Well layout={'vertical'} theme={'white'} width={'full'}>
          <div>
            <Label>{t('Keywords')}</Label>
            <AdGroupKeywords
              keywordErrors={rowErrors.keywords}
              keywords={adGroup.keywords}
              showMatchType={true}
            />
          </div>
          <div>
            <Label>{t('Negative keywords')}</Label>
            <AdGroupKeywords
              keywordErrors={rowErrors.keywords}
              keywords={adGroup.negativeKeywords}
            />
          </div>
          <div>
            <Label tooltip={t('Sitelinks are shared by every ad in this ad group.')}>
              {t('Sitelinks')}
            </Label>
            <AdGroupSitelinks sitelinks={adGroup.sitelinks} />
          </div>
        </Well>
      </Card.Content>
      <Card.Content>
        <Label weight={'medium'}>{t('Ads in this ad group')}</Label>
        {adGroup.ads.length ? (
          <Stack gap={theme.spacing['4']}>
            {adGroup.ads.map((ad, index) => (
              <AdPreview
                ad={ad}
                businessName={businessName}
                key={ad.key}
                name={`ad-group-ad-${index + 1}-preview`}
                sitelinks={adGroup.sitelinks}
              />
            ))}
          </Stack>
        ) : (
          <Text.SmallRegular>{t('No ads yet.')}</Text.SmallRegular>
        )}
      </Card.Content>
    </>
  );
}

export default AdGroupPreview;
